import { Currency } from '~/types/common';

const CURRENCY_MARKERS: Record<Currency, RegExp> = {
  EUR: /^(?:EUR|€)$/i,
  USD: /^(?:USD|US\$|\$)$/i,
  GBP: /^(?:GBP|£)$/i,
  PLN: /^(?:PLN|zł)$/i,
  ZAR: /^(?:ZAR|R)$/i,
};

export const parseTicketPrice = (text: string, currency: Currency): number | null => {
  const normalized = text.replace(/[\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '').trim();
  const marker = normalized.replace(/[\d\s.,]/g, '');

  if (!CURRENCY_MARKERS[currency].test(marker)) return null;

  const amount = normalized.replace(/[^\d.,]/g, '');
  // Accept grouped integers and decimal prices without guessing malformed amounts.
  const decimal = amount.match(/([.,])(\d{1,2})$/);
  const integer = decimal ? amount.slice(0, -decimal[0].length) : amount;

  if (!/^(?:\d+|\d{1,3}(?:,\d{3})+|\d{1,3}(?:\.\d{3})+)$/.test(integer)) return null;
  if (decimal && integer.includes(decimal[1])) return null;

  const value = Number(`${integer.replace(/[.,]/g, '')}${decimal ? `.${decimal[2]}` : ''}`);

  return Number.isFinite(value) && value > 0 ? value : null;
};

const isDisplayed = (element: HTMLElement) => {
  if (!element.isConnected || !element.getClientRects().length) return false;

  let current: Element | null = element;

  while (current) {
    const style = window.getComputedStyle(current);

    if (current.hasAttribute('hidden') || style.display === 'none'
      || style.visibility === 'hidden' || style.visibility === 'collapse' || style.opacity === '0') return false;

    const root = current.getRootNode();
    current = current.parentElement || (root instanceof ShadowRoot ? root.host : null);
  }

  return true;
};

export const getLowestDisplayedTicketPrice = (
  roots: Array<HTMLElement | ShadowRoot>,
  currency: Currency,
): number | null => {
  let lowest: number | null = null;
  let hasUnknownPrice = false;

  roots.forEach((root) => {
    root.querySelectorAll<HTMLElement>(
      '[class*="TicketsWidget-module__wrapper"] [data-role="flight-card"] [class*="FlightCard-module__cardLeftPrice"]',
    ).forEach((element) => {
      if (!isDisplayed(element)) return;

      const price = parseTicketPrice(element.textContent || '', currency);

      if (price === null) {
        hasUnknownPrice = true;
      } else {
        lowest = lowest === null ? price : Math.min(lowest, price);
      }
    });
  });

  // Do not advertise savings while visible fares cannot be compared safely.
  return hasUnknownPrice ? null : lowest;
};